import { useState } from 'react';
import { IAMUserInfo, UserInfo } from '../../types';
import profileLogo from '../../assets/profile/profile-img.png';
import { Loader, NoDataError } from '../NoDataError/NoDataError';
import * as Styles from './styles';
import { UseQueryResult } from 'react-query';
import LogoutConfirmDialog from '../Dialogs/LogoutConfirmDialog';
import { useChannelMessage } from '../MessageProvider/MessageContext';
import { LOGOUT_STATUS } from '../MessageProvider/MessageProvider';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/Theme';
import { ExtendedProfileHeader, ExtendedTitle } from './Title';

export default function ProfileContent(props: {
  oktaUser: UseQueryResult;
  iamUser: UseQueryResult;
}) {
  const { oktaUser, iamUser } = props;
  const [showLogoutDialog, setShowLogoutDialog] = useState<boolean>(false);
  const { logoutProducts, setLogoutStatus } = useChannelMessage();

  const oktaUserInfo = (oktaUser.data || {}) as UserInfo;
  const iamUserInfo = (iamUser.data || {}) as IAMUserInfo;

  const empId = iamUserInfo?.fullUser?.user?.employeeId;
  const defaultStore = iamUserInfo?.fullUser?.stores?.filter(
    (store) => store.defaultStore
  );
  const storeNMame = defaultStore?.length && defaultStore[0]?.name;
  const iamDataExists = empId || storeNMame;
  const {t} = useTranslation();
  const { darkMode } = useTheme();

  const handleSignOut = () => {
    setShowLogoutDialog(false);
    logoutProducts();
  };

  const getPhoneNumber = (contactList: any, oktaPhone: string | undefined) => {
    return oktaPhone && oktaPhone !== '' ? oktaPhone : (contactList && contactList.length > 0
      ? (contactList.find((x: any) => x.typeCode === 'Phone' && !x.secondary)?.value
      || contactList.find((x: any) => x.typeCode === 'Phone' && x.secondary)?.value
      || '')
      : '');
  }

  const UserProfile = (
    <>
      <ExtendedProfileHeader themeType={darkMode}>
        <ExtendedTitle themeType={darkMode}>
          <Styles.ProfileImage data-testid="user-img" src={profileLogo} />
          <div>
            <h2 data-testid="user-name">
              {oktaUserInfo.userProfile?.firstName} {oktaUserInfo.userProfile?.lastName}
            </h2>
            <h4>{iamUserInfo?.fullUser?.enterprise?.name}</h4>
          </div>
        </ExtendedTitle>
      </ExtendedProfileHeader>
      <Styles.ProfileBody>
        {iamDataExists && (
          <>
            <h2>{t('profile.details')}</h2>
            <Styles.Details>
              {empId && (
                <>
                  <Styles.Label data-testid="label-phone">
                    {t('profile.empID')}
                  </Styles.Label>
                  <Styles.Value data-testid="value-phone">{empId}</Styles.Value>
                </>
              )}
              {storeNMame && (
                <>
                  <Styles.Label data-testid="label-email">
                    {t('profile.store')}
                  </Styles.Label>
                  <Styles.Value data-testid="value-email">
                    &nbsp;
                    {storeNMame}
                  </Styles.Value>
                </>
              )}
            </Styles.Details>
          </>
        )}
        <h2>{t('profile.contactInfo')}</h2>
        <Styles.Details>
          <Styles.Label data-testid="label-phone">
            {t('profile.phone')}
          </Styles.Label>
          <Styles.Value data-testid="value-phone">
            {getPhoneNumber(iamUserInfo.fullUser?.contacts, oktaUserInfo.userProfile?.mobilePhone)}
          </Styles.Value>
          <Styles.Label data-testid="label-email">
            {t('profile.email')}
          </Styles.Label>
          <Styles.Value data-testid="value-email">
            &nbsp;
            {oktaUserInfo.userProfile?.email}
          </Styles.Value>
        </Styles.Details>
      </Styles.ProfileBody>
      </>
  );

  return (
    <>
      <LogoutConfirmDialog
        isOpen={showLogoutDialog}
        onLogout={handleSignOut}
        onCancel={() => {
          setLogoutStatus(LOGOUT_STATUS.NONE);
          setShowLogoutDialog(false);
        }}
      />
      {oktaUser.isError ? (
        <NoDataError
          headerText={t('profile.unavailable')}
        />
      ) : oktaUser.isLoading || iamUser.isLoading ? (
        <Loader />
      ) : (
        UserProfile
      )}
    </>
  );
}
